import { MinusCircle, PlusCircle } from 'lucide-react'
import { useFormContext } from 'react-hook-form'

import { cn } from '@fortem-monorepo/js-utilities'
import { FormError } from '@fortem-monorepo/react-ui'
import { Button } from '@fortem-monorepo/react-ui'
import { FormDescription, FormField, FormItem, FormLabel } from '@fortem-monorepo/react-ui'
import { Skeleton } from '@fortem-monorepo/react-ui'

import { GuidelineFormValues } from '@/pages/funds/ic-guidelines-v2/components/edit-guideline-form/schema'
import { useFunds } from '@/react-query/queries/funds/funds'

export interface FundsFieldProps {}

export const FundsField: React.FC<FundsFieldProps> = ({}) => {
  const { control, setValue } = useFormContext<GuidelineFormValues>()

  const { data: funds, query: fundsQuery } = useFunds()

  return (
    <FormField
      name="funds"
      control={control}
      render={({ field: { value } }) => {
        return (
          <FormItem>
            <div className="flex items-center gap-2 overflow-hidden">
              <FormLabel>Funds</FormLabel>
              <FormError />
            </div>
            <div className="flex items-center gap-2">
              <Button
                size="sm"
                variant={'outline'}
                disabled={
                  value.length === funds.length &&
                  value.every((v) => {
                    return funds.map((f) => f.fund_id).includes(v)
                  })
                }
                onClick={() =>
                  setValue(
                    'funds',
                    funds.map((f) => f.fund_id),
                    { shouldValidate: true, shouldDirty: true },
                  )
                }
              >
                Select All <PlusCircle />
              </Button>
              <Button
                size="sm"
                variant={'outline'}
                disabled={value.length === 0}
                onClick={() => setValue('funds', [], { shouldValidate: true, shouldDirty: true })}
              >
                Deselect All <MinusCircle />
              </Button>
              <FormDescription>Assign the funds to use this guideline.</FormDescription>
            </div>
            {fundsQuery.isFetching ? (
              <Skeleton className="rounded-md w-20 h-9" />
            ) : (
              <div className={'gap-2 flex flex-wrap'}>
                {funds.map((fund) => {
                  const checked = (value ?? []).includes(fund.fund_id)

                  const toggleFund = () => {
                    if (value?.includes(fund.fund_id)) {
                      setValue(
                        'funds',
                        [...value].filter((f) => f !== fund.fund_id),
                        { shouldValidate: true, shouldDirty: true },
                      )
                    } else {
                      const newValue = [...(value ?? []), fund.fund_id]
                      setValue('funds', newValue, {
                        shouldValidate: true,
                        shouldDirty: true,
                      })
                    }
                  }

                  return (
                    <div
                      key={fund.fund_name}
                      onClick={() => toggleFund()}
                      className={cn(
                        'inline-flex transition-all duration-100 border px-4 py-2 rounded-lg select-none cursor-pointer w-auto',
                        {
                          'border-green-300 bg-green-50': checked,
                          'border-gray-300 bg-gray-100 hover:bg-gray-200/75': !checked,
                        },
                      )}
                    >
                      <span className={'text-sm'}>{fund.fund_name}</span>
                    </div>
                  )
                })}
              </div>
            )}
          </FormItem>
        )
      }}
    />
  )
}
